import { useCallback, useMemo, useRef, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import type { DipColumnFilterValues } from "~/features/dataImportProjects/types";
import { v2Route } from "~/v2/routes";
import { DataImportProjectListPresenter } from "./DataImportProjectList.presenter";

const DEFAULT_PAGE_SIZE = 25;

export function DataImportProjectListContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.dataImportProjects.list);

  const urlSearch = params.search ?? "";
  const [search, setSearch] = useState(urlSearch);
  const lastUrlSearchRef = useRef(urlSearch);
  if (urlSearch !== lastUrlSearchRef.current) {
    lastUrlSearchRef.current = urlSearch;
    if (urlSearch !== search) setSearch(urlSearch);
  }

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const excludeDeactivated = params.excludeDeactivated ?? true;

  const onSearchChange = useCallback(
    (next: string) => {
      setSearch(next);
      setParams((prev) => ({ ...prev, search: next || undefined, page: undefined }));
    },
    [setParams],
  );

  const columnFilterValues: DipColumnFilterValues = useMemo(
    () => ({
      seq: params.seq ?? "",
      name: params.name ?? "",
      status: params.status ?? "",
      references: params.references ?? "",
      startAtFrom: params.startAtFrom ?? "",
      startAtTo: params.startAtTo ?? "",
      endAtFrom: params.endAtFrom ?? "",
      endAtTo: params.endAtTo ?? "",
    }),
    [
      params.seq,
      params.name,
      params.status,
      params.references,
      params.startAtFrom,
      params.startAtTo,
      params.endAtFrom,
      params.endAtTo,
    ],
  );

  // Single setParams: setSearchParams reads stale params from the render closure, so two
  // synchronous calls clobber each other (the 2nd navigate wins).
  const onApplyFilters = useCallback(
    (next: DipColumnFilterValues, nextExcludeDeactivated: boolean) =>
      setParams((prev) => ({
        ...prev,
        seq: next.seq || undefined,
        name: next.name || undefined,
        status: next.status || undefined,
        references: next.references || undefined,
        startAtFrom: next.startAtFrom || undefined,
        startAtTo: next.startAtTo || undefined,
        endAtFrom: next.endAtFrom || undefined,
        endAtTo: next.endAtTo || undefined,
        excludeDeactivated: nextExcludeDeactivated ? undefined : false,
        page: undefined,
      })),
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next })),
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) =>
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      })),
    [setParams],
  );

  const listState = useMemo(
    () => ({ search, excludeDeactivated, columnFilterValues, pageIndex, pageSize }),
    [search, excludeDeactivated, columnFilterValues, pageIndex, pageSize],
  );

  return (
    <DataImportProjectListPresenter
      listState={listState}
      onSearchChange={onSearchChange}
      onApplyFilters={onApplyFilters}
      onPageIndexChange={onPageIndexChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
